import React, { Component } from 'react';
import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';
import { getLatLng } from 'util/geo';
import isCrawler from 'util/isCrawler';
import cssVars from 'assets/styles/variables.json';
import createMarkerContainerClass from './MarkerContainer';
import haveOptionsChanged from './haveOptionsChanged';
import googleMapStyles from './mapStyle.json';
import config from '../../../config';

function loadGoogleMapsAPI(callback) {
  // Look for existing script element already loading the API
  const scriptSrc = `https://maps.googleapis.com/maps/api/js?key=${config.GOOGLE_MAPS_API_KEY}&callback=setupGoogleMap&libraries=drawing`;
  const existingScript = Array.prototype.reduce.call(
    document.getElementsByTagName('script'),
    (found, elem) => found || (elem.src === scriptSrc ? elem : false),
    false
  );
  if (!existingScript) {
    window.setupGoogleMap = callback;
    const script = document.createElement('script');
    script.src = scriptSrc;
    script.async = true;
    if (document.body) document.body.appendChild(script);
  } else {
    const prevCallback = window.setupGoogleMap;
    window.setupGoogleMap = () => {
      prevCallback();
      callback();
    };
  }
}

function extendBounds(bounds, node) {
  if (!node) return;

  if (node.hasOwnProperty('position') && node.position instanceof Object) {
    bounds.extend(getLatLng(node.position));
    return;
  }

  Object.keys(node).forEach((key) => {
    const value = node[key];
    if (!value || value === node) return;

    if (getLatLng(value)) {
      bounds.extend(getLatLng(value));
    } else if (value.geometry) {
      value.geometry.coordinates.forEach((coords) => {
        // the coordinates are either LatLng or array of LatLng
        if (getLatLng(coords)) {
          bounds.extend(getLatLng(coords));
        } else if (
          Array.isArray(coords) &&
          coords.length > 0 &&
          getLatLng(coords[0])
        ) {
          coords.forEach((coord) => bounds.extend(getLatLng(coord)));
        }
      });
    } else if (value instanceof Object) {
      extendBounds(bounds, value);
    }
  });
}

const SQUAREFOOT_HQ = { lat: 40.741047, lng: -73.992917 };
const defaultMapOptions = {
  styles: googleMapStyles,
  gestureHandling: 'auto',
  maxZoom: 16,
  zoom: 14,
  center: SQUAREFOOT_HQ,
};
const defaultMobileMapOptions = {
  zoomControl: false,
  streetViewControl: false,
  mapTypeControl: false,
};
const mobileBreakpoint = parseInt(cssVars['bp-mobile'], 10);

export default class GoogleMap extends Component {
  state = {};

  static defaultProps = {
    style: null,
    onClick: null,
    children: null,
    className: null,
    onRightClick: null,
    boundsPadding: null,
    autoCenter: true,
    options: {},
  };

  static propTypes = {
    style: PropTypes.object,
    onClick: PropTypes.func,
    children: PropTypes.node,
    options: PropTypes.object,
    autoCenter: PropTypes.bool,
    className: PropTypes.string,
    onRightClick: PropTypes.func,
    boundsPadding: PropTypes.number,
  };

  static childContextTypes = {
    map: PropTypes.any,
  };

  getChildContext() {
    return this.state;
  }

  componentDidMount() {
    // Load and initialize the API only if the user is not a crawler
    if (!isCrawler()) {
      const initializeMap = this.initializeMap.bind(this);
      if (typeof google === 'undefined') {
        loadGoogleMapsAPI(initializeMap);
      } else if (!this.map) {
        setImmediate(initializeMap);
      }
    }
  }

  componentDidUpdate(prevProps) {
    if (!this.ref || typeof google === 'undefined') return;

    if (
      this.props.children &&
      !isEqual(this.props.children, prevProps.children)
    ) {
      this.centerMap();
    }

    if (
      this.state.map &&
      haveOptionsChanged(this.props.options, prevProps.options)
    ) {
      this.state.map.setOptions({
        ...defaultMapOptions,
        ...(this.props.options || {}),
      });
    }
  }

  initializeMap() {
    if (!this.ref) return;

    const mapOptions = defaultMapOptions;
    if (window.innerWidth <= mobileBreakpoint)
      Object.assign(mapOptions, defaultMobileMapOptions);
    Object.assign(mapOptions, this.props.options);
    const map = new google.maps.Map(this.ref, mapOptions);
    if (this.props.onClick)
      google.maps.event.addListener(map, 'click', this.props.onClick);
    if (this.props.onRightClick)
      google.maps.event.addListener(map, 'rightclick', this.props.onRightClick);
    google.maps.event.addDomListener(window, 'resize', () => {
      const center = map.getCenter();
      google.maps.event.trigger(map, 'resize');
      map.setCenter(center);
    });

    // Create map marker container class with google maps API on load
    window.MarkerContainer = createMarkerContainerClass(google);

    this.setState({ map }, this.centerMap);
  }

  centerMap() {
    if (!this.props.children || !this.props.autoCenter) return;

    const bounds = new google.maps.LatLngBounds(null);

    React.Children.forEach(this.props.children, (child) =>
      extendBounds(bounds, child && (child.props || child.attributes))
    );

    if (
      this.state.map &&
      !bounds.isEmpty() &&
      !(this.lastBounds && this.lastBounds.equals(bounds))
    ) {
      this.state.map.fitBounds(bounds, this.props.boundsPadding);
      this.state.map.panToBounds(bounds);
      this.lastBounds = bounds;
    }
  }

  render() {
    return (
      <div className={this.props.className} style={this.props.style}>
        <div
          ref={(ref) => {
            if (this.state.map || ref === null) return;
            this.ref = ref;
          }}
          style={{ width: '100%', height: '100%' }}
        />
        {this.state.map && this.props.children}
      </div>
    );
  }
}
